import { FocusScope } from '@react-aria/focus';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Modal, ModalOverlay } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Card } from 'v5/design-system/Card';
import { cn } from 'v5/platform/dom/cn';
import { usePortalContainer } from 'v5/primitives/PortalContainer';
import {
  SheetStack,
  SheetStackProvider,
  type SheetStackState,
  useSheetStack
} from './ContentSheet';
import { SplitHandle } from './SplitHandle';
import { COLUMN_PX, CONTENT_MIN_PX, fitColumn } from './splitGeometry';
import { SplitViewContext } from './splitViewContext';

interface Room {
  row: number;
  gap: number;
  /** The open sheets' width together; a leaving one's not. */
  sheets: number;
}

/**
 * The room in a row holding a card and a `SheetStack` beside it: the row's width and gap, and
 * the width the open sheets take.
 */
function useRoom(row: HTMLDivElement | null): Room {
  const [room, setRoom] = useState<Room>({ row: 0, gap: 0, sheets: 0 });
  useLayoutEffect(() => {
    const stack = row?.querySelector<HTMLElement>(
      ':scope > [data-sheet-stack]'
    );
    if (!row || !stack) return;
    const slots = () =>
      [...stack.children].filter(
        (slot): slot is HTMLElement =>
          slot instanceof HTMLElement &&
          !slot.hasAttribute('data-stack-exiting')
      );
    const measure = () => {
      const next: Room = {
        row: row.clientWidth,
        gap: parseFloat(getComputedStyle(row).columnGap) || 0,
        sheets: slots().reduce((sum, slot) => sum + slot.offsetWidth, 0)
      };
      setRoom(prev =>
        prev.row === next.row &&
        prev.gap === next.gap &&
        prev.sheets === next.sheets
          ? prev
          : next
      );
    };
    // a slot is empty when it mounts and takes its width once its sheet's content is portaled in.
    // Applied before the frame is painted (`flushSync`): a sheet must not show over the card for a
    // frame before the card makes room
    const measureNow = () => flushSync(measure);
    const resizes = new ResizeObserver(measureNow);
    const observeAll = () => {
      resizes.disconnect();
      resizes.observe(row);
      slots().forEach(slot => resizes.observe(slot));
    };
    const slotChanges = new MutationObserver(() => {
      observeAll();
      measureNow();
    });
    slotChanges.observe(stack, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-stack-exiting']
    });
    observeAll();
    measure();
    return () => {
      resizes.disconnect();
      slotChanges.disconnect();
    };
  }, [row]);
  return room;
}

/** The sheets stand beside the card, narrowing it, while it keeps its least width. */
const sheetsBesideCard = (room: Room, reservedPx: number) =>
  room.sheets > 0 &&
  room.row - reservedPx - (room.sheets + room.gap) >= CONTENT_MIN_PX;

export interface SplitViewLayoutProps {
  /** The page's own content, in the card the sheets stand at. */
  content: ReactNode;
  /** A `SplitViewColumn` beside the card, or nothing. */
  column?: ReactNode;
  /** The user's choice to open the column in a dialog (`SplitViewColumn.Maximize`). */
  columnMaximized?: boolean;
  onColumnMaximizedChange?: (maximized: boolean) => void;
  /** The column's width beside the card, as the user left it. */
  columnWidth?: number;
  /** The user dragged the column's border; without it the border stays put. */
  onColumnWidthChange?: (width: number) => void;
  /**
   * Panels filling the container edge to edge (a dialog's body), not cards on a ground: no gap,
   * a line between them, the column on the subtle ground.
   */
  flush?: boolean;
  /** The content stands on the page's ground, not in a card of its own; the column stays a card. */
  plainContent?: boolean;
  className?: string;
}

/**
 * A content card with a column beside it: the column narrows the card, never covers it. Maximized
 * by the user, or on a layout too narrow for both, the column is a modal dialog over the page
 * instead; it is the same column all along, moved, not mounted again, so what it holds (a search,
 * a selection) stays. Sheets opened from the card or the column (`ContentSheet`) stand at the right
 * edge of the card, or of the column in its dialog: beside it, narrowing it, while it keeps its
 * least width; otherwise over its edge. Beside the card, the column's border can be dragged; the
 * card keeps its least width.
 */
export function SplitViewLayout({
  content,
  column,
  columnMaximized = false,
  onColumnMaximizedChange,
  columnWidth = COLUMN_PX,
  onColumnWidthChange,
  flush = false,
  plainContent = false,
  className
}: SplitViewLayoutProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SplitViewLayout' });
  // a sheet coming or going re-renders the layout, never what it holds
  const sheetStack = useSheetStack();
  const dialogSheetStack = useSheetStack();
  const [row, setRow] = useState<HTMLDivElement | null>(null);
  const room = useRoom(row);
  // the width a drag has reached; the user's choice once it ends
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const fit = fitColumn(room.row, room.gap, dragWidth ?? columnWidth);
  const hasColumn = Boolean(column);
  const inDialog = hasColumn && (fit.narrow || columnMaximized);
  const columnBeside = hasColumn && !inDialog;
  if (!columnBeside && dragWidth !== null) setDragWidth(null);
  const dragging = dragWidth !== null;
  const sheetsBeside = sheetsBesideCard(
    room,
    columnBeside ? fit.width + room.gap : 0
  );
  // the border's place is the width of what stands before it
  const besideColumn = (width: number) => room.row - room.gap - width;

  // the column renders into this element wherever it stands, beside the card or in the dialog:
  // the element moves, the column's React tree stays, and so does its state
  const [columnHost] = useState(() => {
    const element = document.createElement('div');
    element.className = 'flex min-h-0 min-w-0 flex-1';
    return element;
  });
  // moved, a focused control loses the focus: it is noted as its slot lets go (still in the
  // document then) and focused again where the column now stands
  const focusedRef = useRef<HTMLElement | null>(null);
  const holdColumn = useCallback(
    (slot: HTMLElement | null) => {
      if (!slot) {
        const focused = document.activeElement;
        focusedRef.current =
          focused instanceof HTMLElement && columnHost.contains(focused)
            ? focused
            : null;
        return;
      }
      if (columnHost.parentElement === slot) return;
      slot.appendChild(columnHost);
      const focused = focusedRef.current;
      focusedRef.current = null;
      if (focused && columnHost.contains(focused)) {
        focused.focus({ preventScroll: true });
      }
    },
    [columnHost]
  );

  return (
    <SheetStackProvider stack={sheetStack}>
      <SplitViewContext.Provider
        value={{
          inDialog,
          canMaximize: !fit.narrow,
          maximized: columnMaximized,
          onMaximizedChange: onColumnMaximizedChange,
          flush
        }}
      >
        <div
          ref={setRow}
          className={cn(
            'relative isolate flex min-h-0 min-w-0 flex-1',
            !flush && 'gap-3',
            dragging && 'cursor-col-resize select-none',
            className
          )}
          style={{ '--split-column': `${fit.width}px` } as CSSProperties}
        >
          {/* its width changes at once, never through a transition: content laid out by width
              (a grid's auto-fill columns) would re-lay out on every frame; the sheets slide */}
          {flush || plainContent ? (
            <div
              className={cn(
                // `isolate`: what the content raises (a stuck bar) stays under the sheets
                'isolate flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
                flush && 'bg-bg-default',
                dragging && 'pointer-events-none'
              )}
              style={{
                marginRight: sheetsBeside ? room.sheets + room.gap : 0
              }}
            >
              {content}
            </div>
          ) : (
            <Card
              outlined
              className={cn(
                'isolate flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
                dragging && 'pointer-events-none'
              )}
              style={{
                marginRight: sheetsBeside ? room.sheets + room.gap : 0
              }}
            >
              {content}
            </Card>
          )}
          {/* over the card's box, beside the card rather than inside it, so a sheet wider than a
              narrow card runs out to its left instead of being clipped; beside the card, in the
              room it leaves, when it has it */}
          <SheetStack
            records={sheetStack.records}
            beside={sheetsBeside}
            flush={flush}
            className={cn(
              columnBeside &&
                (flush
                  ? 'right-(--split-column)'
                  : 'right-[calc(var(--split-column)+var(--spacing)*3)]')
            )}
          />
          {columnBeside && onColumnWidthChange && (
            <SplitHandle
              value={besideColumn(fit.width)}
              min={besideColumn(fit.max)}
              max={besideColumn(fit.min)}
              onResize={value => setDragWidth(besideColumn(value))}
              onCommit={value => {
                setDragWidth(null);
                onColumnWidthChange(besideColumn(value));
              }}
              onReset={() => onColumnWidthChange(COLUMN_PX)}
              label={t('resize')}
              hint={t('resizeHint')}
              // the gap before the column, or astride the line flush, wider for a finger
              className={cn(
                'absolute inset-y-0 right-(--split-column) z-10 w-3 pointer-coarse:w-6',
                flush
                  ? '-mr-1.5 pointer-coarse:-mr-3'
                  : 'pointer-coarse:-mr-1.5'
              )}
            />
          )}
          {columnBeside && (
            <div
              ref={holdColumn}
              className={cn(
                'flex min-h-0 w-(--split-column) shrink-0',
                dragging && 'pointer-events-none'
              )}
            />
          )}
        </div>
        {inDialog && (
          <ColumnDialog stack={dialogSheetStack} holdColumn={holdColumn} />
        )}
        {hasColumn &&
          createPortal(
            // the column's sheets stand where the column does
            <SheetStackProvider
              stack={inDialog ? dialogSheetStack : sheetStack}
            >
              {column}
            </SheetStackProvider>,
            columnHost
          )}
      </SplitViewContext.Provider>
    </SheetStackProvider>
  );
}

/**
 * The column's place when it is a dialog: a modal dialog as the app's others look, the column
 * flat inside it, the sheets it opens flush at its right edge, beside the column or over its
 * edge as on the page. Escape is the column's and its sheets' own (the column closes, a sheet
 * first); a press outside does nothing.
 */
function ColumnDialog({
  stack,
  holdColumn
}: {
  stack: SheetStackState;
  holdColumn: (slot: HTMLElement | null) => void;
}) {
  const [row, setRow] = useState<HTMLDivElement | null>(null);
  const room = useRoom(row);
  const sheetsBeside = sheetsBesideCard(room, 0);
  return (
    <ModalOverlay
      isOpen
      isKeyboardDismissDisabled
      UNSTABLE_portalContainer={usePortalContainer()}
      className="fixed top-0 left-0 z-50 flex h-(--visual-viewport-height) w-full items-center justify-center bg-charcoal-dark/80 p-4 duration-200 data-entering:animate-in data-entering:fade-in-0 max-sm:p-0"
    >
      <Modal className="flex h-[80vh] max-h-full min-h-144 w-full max-w-360 overflow-hidden border border-border-default bg-bg-default text-fg-default shadow-lg duration-200 data-entering:animate-in data-entering:zoom-in-95 sm:rounded-lg max-sm:h-full">
        <div
          ref={setRow}
          className="relative isolate flex min-h-0 min-w-0 flex-1"
        >
          {/* the focus goes into the column as it arrives, unless it came along with it */}
          <FocusScope autoFocus>
            <div
              ref={holdColumn}
              className="flex min-h-0 min-w-0 flex-1"
              style={{
                marginRight: sheetsBeside ? room.sheets + room.gap : 0
              }}
            />
          </FocusScope>
          {/* the slots tell this stack, not the page's, where they are */}
          <SheetStackProvider stack={stack}>
            <SheetStack records={stack.records} beside={sheetsBeside} flush />
          </SheetStackProvider>
        </div>
      </Modal>
    </ModalOverlay>
  );
}
